/* eslint-disable max-len */

import {IPhoneCodeValidationConfig} from './IUtils';
import {DEFAULT_VALID_NUMBER_OF_YEARS_IN_THE_FUTURE} from 'brand/app/constants';
import {getCardType} from 'coreUtils/utils';
import {validateDate} from 'coreUtils/dates';

const validateEmail = (email: string): boolean => {
	return !!String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		);
};

const validatePostalCode = (postalCode: string): boolean => {
	const regexs: RegExp[] = [
		/^\d{3,8}$/,
		/^\d{5}(-\d{3,4})?$/,
		/^\d{4}-?\d{3}$/,
		/^\d{3}(\s|-)?\d{2}$/,
		/^\d{3}-?\d{4}$/,
		/^\d{2}-?\d{3}$/,
		/^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1}((\s|-)?\d{1}[A-Z]{1}\d{1})?$/,
		/^(([gG][iI][rR]{0,}0[aA]{2})|((([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]?\d\d?)|(([a-pr-uwyzA-PR-UWYZ]\d[a-hjkstuwA-HJKSTUW])|([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]\d[abehmnprv-yABEHMNPRV-Y]))) {0,}\d[abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2}))$/i,
		/^[BKTP][A-Z]\d{4}$/,
		/^(([A-Za-z]\d{2})|([A-Za-z]\d[A-Za-z]))\s[A-Za-z\d]{4}$/,
		/^(nl[-\s]?)?[\d]{4}\s*[a-z]{0,2}$/i,
		/^([a-z]{2}[-\s])?[\d]{4}$/i,
		/^[A-Za-z]\d{4}[A-Za-z]{3}$/,
	];

	return regexs.some((regex) => regex.test(postalCode));
};

const validateLength = (minLength: number, maxLength: number, value: string): boolean => {
	let result = true;

	if (value) {
		if (minLength && minLength > value.length) {
			result = false;
		}

		if (maxLength && maxLength < value.length) {
			result = false;
		}
	}

	return result;
};

const validateSpecialChars = (value: string): boolean => {
	let result = false;

	if (value) {
		const matches = value.match(RegExp('[$%/()\\[\\]{}=?!.,_*\\|+~#@]', 'g'));

		result = matches?.length > 0;
	}

	return result;
};

const validateSpecialCharsLength = (minLength: number, maxLength: number, value: string): boolean => {
	let result = true;

	if (value) {
		const matches = value.match(RegExp('[$%/()\\[\\]{}=?!.,\\-_*\\|+~#@]', 'g'));

		if (minLength && (!matches || minLength > matches.length)) {
			result = false;
		}

		if (maxLength && (!matches || maxLength < matches.length)) {
			result = false;
		}
	}

	return result;
};

const validateCheckBalanceCode = (str: string): boolean => {
	//TODO: write correct validation rule
	return str && true;
};

const validateAmount = (str: string, min?: number, max?: number): boolean => {
	const amount = parseInt(str);

	return (typeof min !== 'number' || min <= amount) && (typeof max !== 'number' || max >= amount);
};

const validateTextarea = (message: string): boolean => (message !== undefined ? message.length <= 250 : true);
const hasLowerCase = (str: string): boolean => /[a-z]/.test(str);
const hasUpperCase = (str: string): boolean => /[A-Z]/.test(str);
const hasLetters = (str: string): boolean => /[a-zA-Z]/.test(str);
const hasNumbers = (str: string): boolean => /[0-9]/.test(str);
const hasNoTags = (str: string): boolean => !/<\/?[a-zA-Z][^>]*>|<!--|-->|<!\[CDATA\[|<!DOCTYPE\b|<\?/i.test(str);

const isVariationGroupId = (str:string): boolean => hasLetters(str);

export const defaultPhoneValidationRules: IPhoneCodeValidationConfig = {
	minLength: 5,
	maxLength: 15,
	regexp: /^[0-9]*$/,
};

const pinLength = 4;

const validatePhoneCode = (phoneCode: string): boolean => defaultPhoneValidationRules.regexp.test(phoneCode);
const validateMonth = (month: number): boolean => month > 0 && month < 13;

const validateExpirationYear = (year: number): boolean => {
	const currentYear = new Date().getFullYear();

	return year >= currentYear && year <= currentYear + DEFAULT_VALID_NUMBER_OF_YEARS_IN_THE_FUTURE;
};

const validateCCExpiration = (year: string, month: string): boolean => {
	const date = new Date();
	const currentYear = date.getFullYear();
	const currentMonth = date.getMonth() + 1;

	if (year === '' || parseInt(year) > currentYear) {
		return true;
	}

	return parseInt(year) === currentYear && parseInt(month) >= currentMonth;
};

const validateCardNumber = (cardNumber: number): boolean => {
	const regex = new RegExp('^[0-9]{13,19}$');

	if (!regex.test(cardNumber.toString())) {
		return false;
	}

	return luhnCheck(cardNumber.toString());
};

const validateAddressId = (address: string): boolean => {
	const specialChars = `,/?!£$%&()="+@#[]{}§<>'*^`;

	return !specialChars.split('').some((char) => address.includes(char));
};

const luhnCheck = (value: string): boolean => {
	let sum = 0;
	let alt = false;
	let i = value.length - 1;
	let num;

	while (i >= 0) {
		num = parseInt(value.charAt(i), 10);

		if (alt) {
			num *= 2;

			if (num > 9) {
				num = (num % 10) + 1;
			}
		}

		alt = !alt;
		sum += num;
		i--;
	}

	return sum % 10 === 0;
};

const validateCardType = (cardNumber: number) => {
	return getCardType(cardNumber);
};

/**
 * This function checks the country code and checksum of the iBAN
 * @param value - iban input
 * @returns boolean
 */
const validateIBAN = (value: string): boolean => {
	// Remove spaces and to upper case
	const iban = value.replace(/ /g, '').toUpperCase();
	const countryCode = iban.substring(0, 2);

	const bbanCountryPatterns = {
		AL: '\\d{8}[\\dA-Z]{16}',
		AD: '\\d{8}[\\dA-Z]{12}',
		AT: '\\d{16}',
		AZ: '[\\dA-Z]{4}\\d{20}',
		BE: '\\d{12}',
		BH: '[A-Z]{4}[\\dA-Z]{14}',
		BA: '\\d{16}',
		BR: '\\d{23}[A-Z][\\dA-Z]',
		BG: '[A-Z]{4}\\d{6}[\\dA-Z]{8}',
		CR: '\\d{17}',
		HR: '\\d{17}',
		CY: '\\d{8}[\\dA-Z]{16}',
		CZ: '\\d{20}',
		DK: '\\d{14}',
		DO: '[A-Z]{4}\\d{20}',
		EE: '\\d{16}',
		FO: '\\d{14}',
		FI: '\\d{14}',
		FR: '\\d{10}[\\dA-Z]{11}\\d{2}',
		GE: '[\\dA-Z]{2}\\d{16}',
		DE: '\\d{18}',
		GI: '[A-Z]{4}[\\dA-Z]{15}',
		GR: '\\d{7}[\\dA-Z]{16}',
		GL: '\\d{14}',
		GT: '[\\dA-Z]{4}[\\dA-Z]{20}',
		HU: '\\d{24}',
		IS: '\\d{22}',
		IE: '[\\dA-Z]{4}\\d{14}',
		IL: '\\d{19}',
		IT: '[A-Z]\\d{10}[\\dA-Z]{12}',
		KZ: '\\d{3}[\\dA-Z]{13}',
		KW: '[A-Z]{4}[\\dA-Z]{22}',
		LV: '[A-Z]{4}[\\dA-Z]{13}',
		LB: '\\d{4}[\\dA-Z]{20}',
		LI: '\\d{5}[\\dA-Z]{12}',
		LT: '\\d{16}',
		LU: '\\d{3}[\\dA-Z]{13}',
		MK: '\\d{3}[\\dA-Z]{10}\\d{2}',
		MT: '[A-Z]{4}\\d{5}[\\dA-Z]{18}',
		MR: '\\d{23}',
		MU: '[A-Z]{4}\\d{19}[A-Z]{3}',
		MC: '\\d{10}[\\dA-Z]{11}\\d{2}',
		MD: '[\\dA-Z]{2}\\d{18}',
		ME: '\\d{18}',
		NL: '[A-Z]{4}\\d{10}',
		NO: '\\d{11}',
		PK: '[\\dA-Z]{4}\\d{16}',
		PS: '[\\dA-Z]{4}\\d{21}',
		PL: '\\d{24}',
		PT: '\\d{21}',
		RO: '[A-Z]{4}[\\dA-Z]{16}',
		SM: '[A-Z]\\d{10}[\\dA-Z]{12}',
		SA: '\\d{2}[\\dA-Z]{18}',
		RS: '\\d{18}',
		SK: '\\d{20}',
		SI: '\\d{15}',
		ES: '\\d{20}',
		SE: '\\d{20}',
		CH: '\\d{5}[\\dA-Z]{12}',
		TN: '\\d{20}',
		TR: '\\d{5}[\\dA-Z]{17}',
		AE: '\\d{3}\\d{16}',
		GB: '[A-Z]{4}\\d{14}',
		VG: '[\\dA-Z]{4}\\d{16}',
	};

	// Check the country code and find the country specific format
	const bbanPattern = bbanCountryPatterns[countryCode];

	if (typeof bbanPattern !== 'undefined') {
		const ibanRegexp = new RegExp('^[A-Z]{2}\\d{2}' + bbanPattern + '$', '');

		if (!ibanRegexp.test(iban)) {
			// Invalid country specific format
			return false;
		}
	}

	// Now check the checksum, first convert to digits
	const ibanCheck = iban.substring(4, iban.length) + iban.substring(0, 4);
	const ibanCheckLength = ibanCheck.length;
	let ibanCheckDigits: string = '';
	let leadingZeroes: boolean = true;

	for (let i = 0; i < ibanCheckLength; i++) {
		const charAt = ibanCheck.charAt(i);

		if (charAt !== '0') {
			leadingZeroes = false;
		}

		if (!leadingZeroes) {
			ibanCheckDigits += '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'.indexOf(charAt);
		}
	}

	// Calculate the result of: ibanCheckDigits % 97
	const ibanCheckDigitsLength = ibanCheckDigits.length;
	let cRest: number | string = '';
	let cOperator: string = '';

	for (let i = 0; i < ibanCheckDigitsLength; i++) {
		const cChar = ibanCheckDigits.charAt(i);

		cOperator = '' + cRest + '' + cChar;

		cRest = parseInt(cOperator) % 97;
	}

	return cRest === 1;
};

/**
 * Checks if user name contains only ASCII characters and umlauts.
 * @param name - customer first or last name
 * @returns boolean
 */
const validateCustomerName = (name: string): boolean => {
	return /^[\x20-\x7EÀ-ÿ]+$/.test(name);
};

const isMatchString = (string, value: string): boolean => {
	return string === value;
};

/**
 * Checks if the string is the integer number
 * @param - string value
 * @returns boolean
 */
const validateInteger = (str: string): boolean => {
	return /^\d+$/.test(str);
};

/**
 * Validates BIC/SWIFT code
 * @param {string} value - BIC/SWIFT code
 * @returns {boolean} - true if the string a valid BIC/SWIFT code
 */
const validateBicSwift = (value: string): boolean => {
	return /^[a-zA-Z]{6}[a-zA-Z0-9]{2,6}$/.test(value);
};

/**
 * Checks if the string is a valid phone number
 * @param {string} value - a phone number
 * @returns {boolean} - true if the string is a phone number
 */
const validatePhoneNumber = (value: string): boolean => {
	return /^\+?\d{1,4}?[-•\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}[-.\s]?\d{4}$/.test(value);
};

/**
 * Checks if the string is a valid phone number or empty
 * @param {string} value - a phone number
 * @returns {boolean} - true if the string is a phone number or empty
 */
const validateOptionalPhoneNumber  = (value: string): boolean => {
	if (!value) {
		return true;
	}

	return validatePhoneNumber(value);
};

/**
 * Checks if the object is a valid birthday
 * @param {object} value - a birthday object
 * @returns {boolean} - true if the string is a phone number
 */
const isBirthday = (value: Record<string, string>) => {
	if (typeof value !== 'object') {
		return false;
	}

	const {day, month, year} = value;

	if (!day || !month || !year) {
		return false;
	}

	return validateDate({
		day: Number(day),
		month: Number(month),
		year: Number(year),
	});
};

/**
 * Checks if the gender is selected
 * @param {number} value - the gender value
 * @returns {boolean} - true if the gender is a valid number and not a string
 */
const isMandatoryGenderSelected = (value: number) => {
	return !isNaN(value) && value >= 0;
};

export default {
	isRequired: (value: string) => typeof value !== 'undefined' && value !== '',
	isTrue: (value: boolean) => value,
	isEmail: (value: string) => validateEmail(value),
	isPhoneNumber: (value: string) => validatePhoneCode(value),
	hasCorrectPhoneNumberLength: (value: string) =>
		validateLength(defaultPhoneValidationRules.minLength, defaultPhoneValidationRules.maxLength, value),
	isPostalCode: (value: string) => validatePostalCode(value),
	isNotEmpty: (value: string) => !!value && /\S/.test(value),
	hasLetters: (value: string) => hasLetters(value),
	hasMixedLetters: (value: string) => hasLowerCase(value) && hasUpperCase(value),
	hasNumbers: (value: string) => hasNumbers(value),
	noNumbers: (value: string) => !hasNumbers(value),
	hasCorrectSpecialCharsLength: (minLength: number, maxLength: number) => (value: string) =>
		validateSpecialCharsLength(minLength, maxLength, value),
	noSpecialChars: (value: string) => !validateSpecialChars(value),
	hasCorrectLength: (minLength: number, maxLength?: number) => (value: string) =>
		validateLength(minLength, maxLength, value),
	isCheckBalanceCode: (value: string) => validateCheckBalanceCode(value),
	isValidAmount: (min?: number, max?: number) => (value: string) => validateAmount(value, min, max),
	textareaMaxLength: (value: string) => validateTextarea(value),
	isEqual: (value: string, valueToCompare: string) => value === valueToCompare,
	isPccCode: (value: string) => /^P[0-9]{12}$/.test(value),
	isMonth: (value: number) => validateMonth(value),
	isValidExpirationYear: (value: number) => validateExpirationYear(value),
	isValidAddressId: (address: string) => validateAddressId(address),
	isCCExpired: (year: string, month: string) => validateCCExpiration(year, month),
	validateCardNumber: (value: number) => validateCardNumber(value),
	validateCardType: (cardType: string) => (value: number) => cardType === validateCardType(value),
	validateCardFormat: (value: number) => !!validateCardType(value),
	validateIBAN: (value: string) => validateIBAN(value),
	validateBicSwift: (value: string) => validateBicSwift(value),
	validateCustomerName: (value: string) => validateCustomerName(value),
	isMatchString: (string: string) => (value: string) => isMatchString(string, value),
	isInteger: (str: string) => validateInteger(str),
	validatePhoneNumber: (value: string) => validatePhoneNumber(value),
	isVariationGroupId: (value: string) => isVariationGroupId(value),
	isBirthday: (value: Record<string, string>) => isBirthday(value),
	validatePin: (value: string) => validateLength(pinLength, pinLength, value),
	validateOptionalPhoneNumber: (value: string) => validateOptionalPhoneNumber(value),
	hasNoTags: (value: string) => hasNoTags(value),
	isMandatoryGenderSelected: (value: number | string) => isMandatoryGenderSelected(value as number)
};
